import { action } from 'mobx';

/* eslint-disable no-use-before-define, no-fallthrough, no-duplicate-case */
import { makeWasmObservable } from 'libs/rendering/gltf/api/wasmObservable';

// import { modalStore } from 'apps/modals/app';
// import { Modal } from 'libs/rendering/gltf/components/modal';
// import { fromLocalStorage } from 'core/localStorage';

import color from 'styles/themes/wikifactoryNew/styles/colors';
import { isMobile } from 'styles/utils';

import { PinConfig, UiModes } from 'libs/rendering/gltf/config';
import { routerStore } from 'core/routing';

import { SectionState } from 'libs/rendering/gltf/components/sectionBrowser/sectionBrowser';

export class ShaderColorOptions {
  // https://doc.instantreality.org/tools/color_calculator/
  // TOOD replace hex codes with new theme lookup after re-brand. color names
  // added in comments already - you're welcome ;)
  constructor() {
    /* FACES */
    this.FaceColorHighlighted = color.wfBlue700;
    this.FaceColorSelected = color.wfBlue400;
    this.FaceColorSelectedAndHighlighted = color.wfYellow;
    // pin notes...
    this.FaceColorIndirectMkpHighlight = color.wfOrange;
    this.FaceColorIndirectPinHighlight = color.wfPurple;
    // navcube
    this.FaceColorNavCubeHighlight = color.wfGrey400;
    this.FaceColorNavCubeSelected = color.wfYellow;

    /* EDGES */
    this.EdgeColorSelected = color.wfBlue;
    this.EdgeColorHighlighted = color.wfPurple;
    this.EdgeColorSelectedAndHighlighted = color.wfRed;
    // edge mark on face sel/high...
    this.EdgeColorCoHighlighted = color.primaryDark;
    this.EdgeColorCoSelected = color.wfBlue;
    this.EdgeColorCoSelectedAndHighlighted = color.primaryDark;
    // pin notes...
    this.EdgeColorIndirectMkpHighlight = color.wfYellow;
    this.EdgeColorIndirectPinHighlight = color.wfPink;
    // navcube
    this.EdgeColorNavCube = color.wfGrey400;
  }

  get options() {
    const obj = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const prop in this) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.hasOwnProperty(prop)) {
        obj[prop] = this[prop];
      }
    }

    const optsStr = JSON.stringify(obj);
    return optsStr;
  }
}

// VIEWER
export class Cad3dViewer {
  constructor(wftyModule, viewerControl) {
    this._holder = null; // HTML wrapper <div> element
    this._canvas = null; // HTML <canvas> element

    this.takeFPSmeasurements = false; // If true, the console log reports every 100 frames the average speed...
    this._hasFile = false;
    this._wasm = null;
    this._cadFile = null;
    this._settings = null;
    this._engine = null;
    this._selectionManager = null;
    this._sectionManager = null;
    this._window = null;
    this._navCube = null;
    this._session = null;

    this.ignorePinAddition = false;

    this.viewerControl = viewerControl;

    // default status
    this._status = new wftyModule.StatusEmbind();

    if (!wftyModule) {
      this._status.error = -1;
      this._status.reason = 'invalid Wikifactory wasm module';
      this.viewerControl.LogFail(this._status.reason);
      return;
    }

    // init color options
    this.colorOptions = new ShaderColorOptions();

    try {
      // instantiate the viewer
      this._wasm = new wftyModule.Cad3dViewerEmbind(this.viewerControl);
    } catch (e) {
      this.viewerControl.LogException(e);
      return;
    }

    if (this._wasm) {
      try {
        // apply colorOptions
        const optsStr = this.colorOptions.options;
        this._wasm.SetColorOptions(optsStr);

        const bckgr3D = color.wfWhite;
        const bckgr2D = color.wfGrey100;
        this._wasm.SetBackgroundColors(bckgr3D, bckgr2D);

        // lighting opions
        // NOTE: oldLighting = { ambient:0.35, diffuse: 0.75, specular: 0, shininess: 32 }
        // Phong shading used. See https://learnopengl.com/Lighting/Basic-Lighting
        const lighting = {
          ambient: 0.35,
          diffuse: 0.75,
          specular: 0.3,
          shininess: 16.0,
        };
        this.setLightingOptions(lighting);

        this.makeWasmObservables();
      } catch (e) {
        this.viewerControl.LogException(e);
      }
    } else {
      this._status.error = -1;
      this._status.reason = 'cannot instantiate Cad3dViewer';
      this.viewerControl.LogFail(this._status.reason);
    }
  }

  makeWasmObservables = () => {
    if (!this._wasm) {
      this.viewerControl.logWarn('cannot create wasm observables');
      return;
    }

    const { obsMap } = this.viewerControl;

    try {
      // make viewer controls @wasmObservable objects
      this._settings = makeWasmObservable(
        obsMap,
        this._wasm.GetView(),
        this.viewerControl,
      );
      this._engine = makeWasmObservable(
        obsMap,
        this._wasm.GetEngine(),
        this.viewerControl,
      );
      this._window = makeWasmObservable(
        obsMap,
        this._wasm.GetWindow(),
        this.viewerControl,
      );
      this._navCube = makeWasmObservable(
        obsMap,
        this._wasm.GetNavCube(),
        this.viewerControl,
      );
      this._selectionManager = makeWasmObservable(
        obsMap,
        this._wasm.GetSelectionManager(),
        this.viewerControl,
      );
      this._sectionManager = makeWasmObservable(
        obsMap,
        this._wasm.GetSectionView(),
        this.viewerControl,
      );
      this._session = makeWasmObservable(
        obsMap,
        this._wasm.GetSession(),
        this.viewerControl,
      );
    } catch (e) {
      this.viewerControl.LogException(e);
    }
  };

  get status() {
    if (this._wasm) {
      return this._status;
    }
    return { error: -1, reason: 'empty viewer' };
  }

  get isOk() {
    return this.status?.error === 0;
  }

  get isPreview() {
    return this.isOk ? this._wasm.isPreview : false;
  }

  set isPreview(b) {
    if (this.isOk) this._wasm.isPreview = b;
  }

  // unique part update
  get needsUniquePartUpdate() {
    try {
      return this.isOk && this._wasm.NeedsUniquePartUpdate();
    } catch (e) {
      this.viewerControl.LogException(e);
    }
    return false;
  }

  autoFocusUniquePartIfNeeded = () => {
    try {
      if (this.needsUniquePartUpdate) this._wasm.AutoFocusUniquePart();
    } catch (e) {
      this.viewerControl.LogException(e);
    }
  };

  get module() {
    return this.viewerControl ? this.viewerControl.wftyModule : null;
  }

  setLightingOptions = lighting => {
    if (lighting) {
      if (
        !Number.isNaN(lighting.ambient) &&
        !Number.isNaN(lighting.diffuse) &&
        !Number.isNaN(lighting.specular) &&
        !Number.isNaN(lighting.shininess)
      ) {
        try {
          this._wasm.SetLightingOptions(lighting);
        } catch (e) {
          this.viewerControl.LogException(e);
        }
      }
    }
  };

  get settings() {
    return this._settings;
  }

  get engine() {
    return this._engine;
  }

  get selectionManager() {
    return this._selectionManager;
  }

  get sectionManager() {
    return this._sectionManager;
  }

  addPinNoteFromLastSelection = () => {
    if (!this.selectionManager.IsSelectionModeLink()) return false;

    const userName = this.viewerControl?.userName;
    if (!userName || userName.length === 0) {
      this.viewerControl.LogException('no user name, cannot add annotation');
      return false;
    }

    if (this.ignorePinAddition === true) {
      // clicking on existing pin note in pin-addition mode...
      this.ignorePinAddition = false;
      return false;
    }

    // 'link' selection, not a selection but a pin note
    let newNote = null;
    let pinTarget = null;
    if (!this._cadFile.notePad.isNull) {
      // get target from last selection
      const lastIx = this.selectionManager.GetSelectionCount() - 1;
      pinTarget = this.selectionManager.GetSelection(lastIx);
      if (pinTarget && pinTarget.HasPoint()) {
        // "optimistic" update, by now we're using a tmporary ID...
        const tempId = PinConfig.pinTempId;
        const existingTemp = this._cadFile.notePad.GetNote(tempId);
        if (existingTemp) {
          // already exists...
          // delete selection used as pin (it's a pin note, not a selection)
          this.selectionManager.DeleteLastSelection();
          this.selectionManager.ClearHighlight();
          return false;
        }

        // overwrite proper target type
        const isFeature = !pinTarget.IsInstance();
        if (isFeature) {
          // pin
          pinTarget.SetIndirectPin();
        } else {
          // indirect
          pinTarget.SetIndirect();
        }

        // create the new note
        this._cadFile.notePad.NewNote(
          tempId,
          pinTarget._wasm,
          userName,
          this.settings._wasm,
        );
        // get the just created note to check if it's there
        newNote = this._cadFile.notePad.GetNote(tempId);

        if (newNote == null || !newNote.IsOk()) {
          this.viewerControl.LogException('error creating new note');
        }
      } else {
        // nothing was selected...
        return false;
      }
    }

    // delete selection used as pin (it's a pin note, not a selection)
    this.selectionManager.DeleteLastSelection();
    this.selectionManager.ClearHighlight();

    // select pin target meanwhile the user enters the text..
    const selModeEnum = this.module.SelectionMode;
    const oldMode = this.selectionManager.GetSelectionMode();
    this.selectionManager.SetSelectionMode(selModeEnum.SelectionModeGeometry); // so we don't fire a pin addition again...
    this.selectionManager.AddSelection(newNote.GetLink().GetTarget()._wasm);
    this.selectionManager.SetSelectionMode(oldMode); // set selection mode back...

    return newNote && newNote.IsOk();
  };

  // window 'cursor' & events management
  get window() {
    return this._window;
  }

  // NavCube position management
  get navCube() {
    return this._navCube;
  }

  // Ui mode (context)
  get mode() {
    try {
      return this.isOk ? this._wasm.GetUiMode() : undefined;
    } catch (e) {
      this.viewerControl.LogException(e);
    }
    return undefined;
  }

  set mode(m) {
    try {
      if (this.isOk) {
        switch (m) {
          case UiModes.assembly:
            this._wasm.SetUiMode(this.module.UiMode.UiModeAssembly);
            break;
          case UiModes.geometry:
            this._wasm.SetUiMode(this.module.UiMode.UiModeGeometry);
            break;
          case UiModes.mesh:
            this._wasm.SetUiMode(this.module.UiMode.UiModeMesh);
            break;
          case UiModes.markup:
            this._wasm.SetUiMode(this.module.UiMode.UiModeMarkup);
            break;
          case UiModes.feature:
            this._wasm.SetUiMode(this.module.UiMode.UiModeFeature);
            break;
          case UiModes.section:
            this._wasm.SetUiMode(this.module.UiMode.UiModeSection);
            break;
          case UiModes.collapsed:
            this._wasm.SetUiMode(this.module.UiMode.UiModeNone);
            break;
          default:
            break;
        }
      }
    } catch (e) {
      this.viewerControl.LogException(e);
    }
  }

  // viewer state serialization
  get session() {
    return this._session;
  }

  get viewState() {
    try {
      return this.isOk ? this._wasm.ToJson() : undefined;
    } catch (e) {
      this.viewerControl.LogException(e);
    }
    return false;
  }

  set viewState(jsonVar) {
    if (this.isOk && jsonVar) {
      let jsonStr = jsonVar;
      if (typeof jsonStr !== 'string') {
        jsonStr = JSON.stringify(jsonVar);
      }

      if (jsonStr.length > 0) {
        try {
          this._wasm.ApplyPatch(jsonStr);
        } catch (e) {
          this.viewerControl.LogException(e);
        }
      }
    }
  }

  // CAD file to render
  @action SetCadFile = cadFile => {
    try {
      if (cadFile) {
        if (!cadFile.isOk) {
          this._status.error = -1;
          this._status.reason = 'invalid cad file';
          window._badfile = cadFile;
          this.viewerControl.LogConversionProblem(this._status.reason);
        } else {
          this._status = this._wasm.SetCadFile(cadFile._wasm);
          this._hasFile = true;
          if (this._status.error !== 0) {
            this.viewerControl.LogConversionProblem(this._status.reason);
          } else {
            this._cadFile = cadFile;
          }

          // set default camera orbit
          this._settings.SetOrbitFixedUp();
        }
      } else {
        this._wasm.SetCadFile(null);
      }
    } catch (e) {
      this.viewerControl.LogException(e);
    }
  };

  get isRendering() {
    try {
      return this.isOk ? this._wasm.IsRendering() : false;
    } catch (e) {
      this.viewerControl.LogException(e);
    }
    return false;
  }

  get isCanvasSet() {
    try {
      return this.isOk && this._wasm.IsCanvasSet();
    } catch (e) {
      this.viewerControl.LogException(e);
    }
    return false;
  }

  // - set the canvas, this can also be used to set the canvas back
  // to nothing, by passing (null, null)
  setCanvas = (holderEl, canvasEl) => {
    if (holderEl && canvasEl) {
      this._holder = holderEl; // HTML wrapper <div> element
      this._canvas = canvasEl; // HTML <canvas> element
      this._wasm.SetCanvas(holderEl.id, canvasEl.id);
      try {
        return this._wasm.IsCanvasSet();
      } catch (e) {
        this.viewerControl.LogException(e);
      }
      return false;
    }

    try {
      this._wasm.SetCanvas('', '');
    } catch (e) {
      this.viewerControl.LogException(e);
    }
    return false;
  };

  // - this sets a flag in wasm which will run code to get the size of the
  // canvas wrapper element and set that size in the wasm window instance
  // - then it will send a UserAction notification back to the js to notify
  // us about a changed canvas, so don't call this in the canvas3D render
  // function
  // - this is responsible for the UpdateCanvasSize: Xpx Ypx messages
  updateCanvas = () => {
    this.window.UpdateCanvas();
  };

  loopCondition = () => {
    if (
      this.viewerControl?.conversionProblem ||
      this.viewerControl?.viewerFailed
    ) {
      return false;
    }

    if (!this._wasm) {
      return false;
    }

    try {
      return this._wasm.LoopCondition();
    } catch (e) {
      this.viewerControl.LogException(e);
    }
    return false;
  };

  renderFrame = () => {
    // - early return to prevent error logs about 'null is not an object'
    // when trying to call RenderFrame after viewer was stopped
    if (!this._wasm) {
      return;
    }

    // - early return when viewer is stopped to now render extra frame
    if (!this.loopCondition()) {
      return;
    }

    try {
      this._wasm.RenderFrame(this.takeFPSmeasurements);
    } catch (e) {
      // - we don't return here after logException because we allow one
      // exception to happen before stopping the renderLoop, that is because
      // we won't to avoid failing the viewer for minor problems that end up
      // being reported to sentry as exception, like for example problems in
      // the wasmObservables code
      this.viewerControl.LogException(e);
    }

    // - once we're here then loopCondition is true and this._wasm is defined,
    // - an exception could have happened, but if there is a serious problem then
    // the next renderFrame call should return early if the viewer is destroyed
    // on if (!this._wasm), or viewerFailed == true on if (!this.loopCondition())
    window.requestAnimationFrame(this.renderFrame);
  };

  // starts rendering
  startRendering = () => {
    if (this.isOk) {
      this.start();
      window.requestAnimationFrame(this.renderFrame);
    }
  };

  start = () => {
    try {
      if (this.isOk) this._wasm.Start();
    } catch (e) {
      this.viewerControl.LogException(e);
    }
  };

  stop = () => {
    try {
      if (this.isOk) this._wasm.Stop();
    } catch (e) {
      this.viewerControl.LogException(e);
    }
  };

  // clear the canvas with a fixed white color, added so I can try working
  // around black flickering by calling this
  clearCanvas = () => {
    try {
      if (this.isOk) this._wasm.ClearCanvas();
    } catch (e) {
      this.viewerControl.LogException(e);
    }
  };

  // reloads rendering assets (GPU state)
  reload = () => {
    try {
      if (this.isOk) {
        const status = this._wasm.Reload();
        return status.error;
      }
    } catch (e) {
      this.viewerControl.LogException(e);
    }

    return false;
  };

  // loads data for newly created tab
  loadFocusedIntem = () => {
    try {
      if (this.isOk) {
        this._wasm.LoadFocusedItem();
      }
    } catch (e) {
      this.viewerControl.LogException(e);
    }
  };

  // free (GPU & CPU) memory used by the Cad3dViewer
  destroy = () => {
    try {
      this._wasm.Destroy();
      this._wasm.delete();
    } catch (e) {
      this.viewerControl?.logWarn(e);
    }

    this.viewerControl.canvasSet = false;
    this._wasm = null;
  };

  /*
  ::::::::::::::::::::::::::::::::::::::::::::::::::::
                'Reverse' callback API
                ----------------------
    c++ calls below JS function(s) when events occur
          so it notifies the JS side about it.
  ::::::::::::::::::::::::::::::::::::::::::::::::::::
  */

  handleUserActionWithTarget = (targetJSON, actionType) => {
    if (!this.isOk) return;
    const actionEnum = this.module.UserActionType;
    if (targetJSON) {
      // here the UI updates stuff given the 'affected' cadItem ...
      switch (actionType) {
        case actionEnum.UserActionHighlight.value:
          this.viewerControl.SelectionUpdated(true);
          break;
        case actionEnum.UserActionSelection.value:
          this.viewerControl.SelectionUpdated(true);
          // pass the target for pin note addition...
          this.addPinNoteFromLastSelection();
          break;
        case actionEnum.UserActionUnSelection.value:
          this.viewerControl.SelectionUpdated(true);
          break;
        case actionEnum.UserActionFocusLoaded.value:
          // focus set
          this.viewerControl.CameraUpdated(true);
          break;
        default:
          this.viewerControl.logWarn(
            `UNKNOWN USER ACTION ${actionType} ${targetJSON}`,
          );
          break;
      }
    }
  };

  handleUserAction = (value, actionType) => {
    if (!this.isOk) return;
    const actionEnum = this.module.UserActionType;

    // here the UI updates stuff...
    switch (actionType) {
      // UNSELECTION
      case actionEnum.UserActionUnSelection.value:
        this.viewerControl.SelectionUpdated(true);
        break;
      case actionEnum.UserActionInspectTabCreated.value:
        // tab created, start brep/mkp json parse...
        this.loadFocusedIntem();
        break;
      case actionEnum.UserActionInspectLoaded.value:
        // tab finished data loading or tab closed (value === -1)...
        this.viewerControl.ViewUpdated(true);
        break;
      /* SELECTION MODE */
      case actionEnum.UserActionSelectionMode.value:
        // selection mode changed...
        // TODO
        break;
      case actionEnum.UserActionStandardView.value:
        // ...
        break;
      case actionEnum.UserActionTessFieldProgress.value:
        this.viewerControl.CameraUpdated(false);
        break;
      default:
        this.viewerControl.logWarn(`unknown user action ${actionType}`);
        break;
    }
  };

  handleUserFlaggedAction = (value, flag, actionType) => {
    if (!this.isOk) return;
    const actionEnum = this.module.UserActionType;

    const newpath = routerStore.route.name;
    const camera = this.viewerControl.viewer.settings.CurrentCamera();
    const focusedInstanceIx = camera?.GetInstanceIndex();

    const { assembly } = this.viewerControl.cadFile;
    const focusedInstance = assembly.GetInstance(focusedInstanceIx);

    switch (actionType) {
      /* DBL CLICK TAB CREATION */
      case actionEnum.UserActionInspect.value:
        this.viewerControl.viewer?.settings?.reportWasmChange();
        // Update the URL
        if (focusedInstance) {
          routerStore.navigate(newpath, {
            ...routerStore.route.params,
            partId: focusedInstance.GetBody().uuid,
          });
        }
        this.viewerControl.ViewUpdated(false);
        break;
      default:
        break;
    }
  };

  handleUserEvent = actionType => {
    if (!this.isOk) return;
    const actionEnum = this.module.UserActionType;

    // here the UI updates stuff...
    switch (actionType) {
      case actionEnum.UserActionUnHighlight.value:
      case actionEnum.UserActionClearSelection.value:
        // clear all highlight...
        this.viewerControl.SelectionUpdated(true);
        break;
      case actionEnum.UserActionUserCameraUnHighlight.value:
      case actionEnum.UserActionUserCameraHighlight.value:
        // user camera highlight change
        this.session?.reportWasmChange();
        break;
      case actionEnum.UserActionCadAssemblyReady.value:
        // assembly ready...
        this.viewerControl.SetAssemblyReady(true);
        break;
      case actionEnum.UserActionCadRequestFinished.value:
        // cad file ready...
        this.viewerControl.ViewUpdated(false);
        this.viewerControl.cadReady = true;
        this.viewerControl.viewerVisible = true;
        this.viewerControl.SyncSelectionMode();

        // hide/show nav cube
        if (!this.settings.CurrentCamera()) {
          break;
        }

        if (
          this.settings.CurrentCamera().Is3D() &&
          !this.settings.CurrentCamera().IsPlaneXY() &&
          !this.viewerControl.isPreview
        ) {
          this.settings.SetNavCubeVisible(!isMobile());
        } else {
          this.settings.SetNavCubeVisible(false);
        }

        this.sectionState = new SectionState(
          this._sectionManager,
          this._engine,
          this.settings.CurrentCamera().GetSceneRadius(),
        );

        // cad-ready-promise for previews
        this.viewerControl.___sceneReadyPromiseResolve();
        // this.viewerControl.updateLightModal();
        break;
      case actionEnum.UserActionCameraMVP.value:
        // camera view-projection matrices changed...
        this.viewerControl.CameraUpdated(true);
        break;
      case actionEnum.UserActionFocusLoaded.value:
        // focus unset (unfocus)
        this.viewerControl.CameraUpdated(true);
        break;
      // PATCH MANAGER
      case actionEnum.UserActionPatchManagerUpdate.value:
        // pin notes...
        break;
      case actionEnum.UserActionTessFieldFinished.value:
        // mesh analysis finished
        this.viewerControl.CameraUpdated(true);
        break;
      case actionEnum.UserActionCanvasSize.value:
        // canvas (window) size change, make Cnavas3D to render...
        this.viewerControl.ViewUpdated(false);
        break;
      default:
        break;
    }
  };
}
